import { UploadAndDisplayImage } from "../../components/uploadAndDisplayImage"
import { JobbinInputGrey } from "../../components/jobbinInputGrey"
import { JobbinPrimaryButton } from "../../components/primaryButton"
import { ToggleBox } from "../../components/toggleBox"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { CreateJobRequest } from "./CreateJobRequest"
import { useGetJobCategories } from "../../hooks/gig-hooks"
import Select from "react-select"
import AsyncSelect from "react-select/async"
import { DateRangePicker } from "../../components/dateRangePicker"
import * as authService from "../../services/auth-service"
import { LoadingComponent } from "../../components/loading"
import * as gigService from "../../../src/services/gig-service"
import { GigPreview } from "../../hooks/models/GigPreview"
import { JobbinSecondaryButton } from "../../components/secondaryButton"
import { PlacesAutocomplete } from "../../components/placesAutocomplete"
import { useToast } from "../../context/toast"
import { useCredits } from "../../context/credits"
import PromoteDurationModal from "../../components/promoteDurationModal"
import {
  Option,
  CONTRACT_TYPE_OPTIONS,
  EXPERIENCE_OPTIONS,
  WORK_LOCATION_TYPE_OPTIONS,
  PAY_UNIT_OPTIONS,
  GROSS_NET_OPTIONS,
  payUnitFromRateType,
  grossNetFromRateType,
  composeRateType,
} from "./options"
import {
  FieldErrors,
  FieldKey,
  STEP_FIELDS,
  STEP_TITLES,
  STEP_SUBTITLES,
  DESCRIPTION_MAX,
  errorsForStep,
  validateFields,
} from "./validation"
import { FormField } from "./components/FormField"
import { Segmented } from "./components/Segmented"

/** Format a Date as a local "YYYY-MM-DD" string (no UTC shift, unlike toISOString). */
const toLocalDateString = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Today as a local "YYYY-MM-DD" string, used as a min for date inputs. */
const todayLocalDateString = (): string => toLocalDateString(new Date())

export const JobPage = () => {
  const [rate, setRate] = useState("")
  const [gigTitle, setGigTitle] = useState<Option | null>(null)
  const [location, setLocation] = useState("")
  const [category, setCategory] = useState("")
  const [positions, setPositions] = useState<Option[]>([])
  const [rateType, setRateType] = useState("")
  const [description, setDescription] = useState("")
  const [gigId, setGigId] = useState<string | null>(null)
  const [dateTo, setDateTo] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [images, setImages] = useState<File[]>([])
  const [videoNecessary, setVideoNecessary] = useState(false)
  const [cvNecessary, setCvNecessary] = useState(false)
  const [confidentialRate, setConfidentialRate] = useState(false)
  const [negotiableRate, setNegotiableRate] = useState(false)
  const [jobType, setJobType] = useState("")
  const [contractType, setContractType] = useState("")
  const [experience, setExperience] = useState("")
  const [address, setAddress] = useState("")
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)
  const [workLocationType, setWorkLocationType] = useState("")
  const [createGigIsLoading, setCreateGigIsLoading] = useState(false)
  const [gigDetails, setGigDetails] = useState<GigPreview>()
  const [isPromoted, setIsPromoted] = useState(false)
  const [showPromoteDurationModal, setShowPromoteDurationModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [quota, setQuota] = useState<gigService.JobPostingQuota | null>(null)
  const [durationDays, setDurationDays] = useState<number>(14)

  // Treptele de durată oferite, limitate la cele care încap până la data de încheiere a jobului
  // (dateTo). Pentru un job care se termină curând n-are sens — și ar fi taxat — un anunț de 60 zile.
  const durationDaysOptions = useMemo(() => {
    const opts = quota?.durationOptions ?? []
    if (opts.length === 0 || !dateTo) return opts
    const daysUntilEnd = Math.ceil((new Date(dateTo + "T23:59:59").getTime() - Date.now()) / 86400000)
    const fitting = opts.filter(o => o.days <= daysUntilEnd)
    return fitting.length > 0 ? fitting : [opts[0]] // păstrează mereu cel puțin prima treaptă
  }, [quota, dateTo])

  const durationCappedByDate = !!dateTo && !!quota
    && durationDaysOptions.length < quota.durationOptions.length

  // dacă treapta selectată nu mai încape până la dateTo, sari pe cea mai mare disponibilă
  useEffect(() => {
    if (durationDaysOptions.length === 0) return
    if (!durationDaysOptions.some(o => o.days === durationDays)) {
      setDurationDays(durationDaysOptions[durationDaysOptions.length - 1].days)
    }
  }, [durationDaysOptions]) // eslint-disable-line react-hooks/exhaustive-deps
  const [errors, setErrors] = useState<FieldErrors>({})
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { notify } = useToast()
  const { balance: creditBalance, pricing, refresh: refreshCredits } = useCredits()
  const eventId = searchParams.get("eventId") ?? undefined
  const eventTitle = searchParams.get("eventTitle") ?? undefined
  const eventDateFromParam = searchParams.get("eventDateFrom") ?? undefined
  const eventDateToParam = searchParams.get("eventDateTo") ?? undefined
  const eventCityName = searchParams.get("eventCityName") ?? undefined

  const eventMinDate = eventDateFromParam ? new Date(eventDateFromParam) : null
  const eventMaxDate = eventDateToParam ? new Date(eventDateToParam) : null

  const { categories } = useGetJobCategories()

  const clearError = (key: FieldKey) =>
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })

  const formValues = useMemo(
    () => ({
      category,
      gigTitle: gigTitle ? gigTitle.label : null,
      experience,
      description,
      location,
      workLocationType,
      address,
      contractType,
      dateFrom,
      dateTo,
      rateType,
      rate,
      confidentialRate,
    }),
    [category, gigTitle, experience, description, location, workLocationType, address, contractType, dateFrom, dateTo, rateType, rate, confidentialRate]
  )

  const payUnit = payUnitFromRateType(rateType)
  const grossNet = grossNetFromRateType(rateType)

  const onImageSelected = (img: File) => {
    setImages((prev) => {
      if (prev.some((image) => image.name === img.name)) return prev
      return [...prev, img].slice(0, 5)
    })
  }

  const onImageRemoved = (img: File) => {
    setImages((prev) => prev.filter((image) => image.name !== img.name))
  }

  const uploadImagesToR2 = async (imgs: File[]): Promise<{ fileNames: string[], publicUrls: string[] }> => {
    const fileNames: string[] = []
    const publicUrls: string[] = []
    for (const img of imgs) {
      const extension = img.name.split(".").pop() || ""
      const presign = await gigService.presignUpload(extension)
      await gigService.uploadToR2(presign.uploadUrl, img)
      fileNames.push(presign.fileName)
      publicUrls.push(presign.publicUrl)
    }
    return { fileNames, publicUrls }
  }

  /** Validates the whole form, surfaces per-field errors and jumps to the first bad step. */
  const ensureValid = (): boolean => {
    const found = validateFields(formValues)
    setErrors(found)
    if (Object.keys(found).length === 0) return true
    const firstBadStep = STEP_FIELDS.findIndex((keys) => keys.some((k) => found[k]))
    if (firstBadStep >= 0) setCurrentStep(firstBadStep)
    notify("Mai sunt câmpuri obligatorii de completat.", "info")
    return false
  }

  const goToNextStep = () => {
    const stepErrors = errorsForStep(validateFields(formValues), currentStep)
    if (Object.keys(stepErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...stepErrors }))
      return
    }
    setCurrentStep((s) => Math.min(s + 1, STEP_TITLES.length - 1))
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const goToPreviousStep = () => {
    setCurrentStep((s) => Math.max(s - 1, 0))
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  /** Picking a contract type sets a sensible default pay unit (once). */
  const handleContractTypeChange = (val: string) => {
    setContractType(val)
    if (val === "INDETERMINATE") setDateTo("")
    if (!rateType) setRateType(val === "DETERMINATE" ? "DAY" : "GROSS")
    clearError("contractType")
    clearError("period")
  }

  const doSendJob = async () => {
    if (!ensureValid()) return

    setCreateGigIsLoading(true)

    try {
      const { fileNames, publicUrls } = await uploadImagesToR2(images)

      const newGig: CreateJobRequest = {
        rate,
        gigTitle: gigTitle!.label,
        location,
        category,
        rateType,
        description,
        cvNecessary,
        dateTo: dateTo ? dateTo + "T00:00:00" : undefined,
        videoNecessary,
        dateFrom: dateFrom ? dateFrom + "T00:00:00" : undefined,
        negotiableRate,
        volunteerWork: false,
        jobType,
        contractType,
        experience,
        confidentialRate,
        workLocationType,
        address,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        eventId: eventId,
        durationDays,
      }

      const response = await gigService.createGigPresigned(newGig, fileNames, publicUrls)

      if (response.ok) {
        notify("Job-ul a fost adăugat cu succes!", "success")
        navigate(eventId ? `/events/${eventId}` : "/jobs")
      } else {
        let code = ""
        try {
          const body = await response.json()
          code = body?.["error-code"] ?? ""
        } catch {}
        if (response.status === 400 && code === "insufficient.credits") {
          notify("Nu ai destule credite pentru a posta. Cumpără un pachet și încearcă din nou.", "error")
          navigate("/credits")
        } else {
          notify("A apărut o eroare. Te rugăm să contactezi suportul pentru mai multe informații.", "error")
          navigate(eventId ? `/events/${eventId}` : "/home")
        }
      }
    } catch {
      notify("A apărut o eroare. Te rugăm să contactezi suportul pentru mai multe informații.", "error")
      navigate(eventId ? `/events/${eventId}` : "/home")
    } finally {
      setCreateGigIsLoading(false)
    }
  }

  const cityDebounceTimer = useRef<ReturnType<typeof setTimeout>>()

  const loadCityOptions = (inputValue: string): Promise<Option[]> => {
    return new Promise((resolve) => {
      if (cityDebounceTimer.current) clearTimeout(cityDebounceTimer.current)
      if (!inputValue || inputValue.length < 2) {
        resolve([])
        return
      }
      cityDebounceTimer.current = setTimeout(async () => {
        const cities = await authService.searchCities(inputValue)
        resolve(
          cities.map((city: { id: string; name: string; county?: string }) => ({
            value: city.id,
            label: city.county ? `${city.name}, ${city.county}` : city.name,
          }))
        )
      }, 300)
    })
  }

  const getCategoryOptions = () => {
    const defaultOptions: Option[] = []
    categories.forEach((categoryItem) => {
      defaultOptions.push({ value: categoryItem.id, label: categoryItem.name })
    })
    return defaultOptions
  }

  const handleCategoryChange = async (newValue: Option | null) => {
    setCategory(newValue?.label || "")
    setGigTitle(null)
    setPositions([])
    clearError("category")
    if (newValue?.value) {
      const titles: string[] = await gigService.getGigTitlesByCategory(newValue.value)
      setPositions(titles.map((t) => ({ value: t, label: t })))
    }
  }

  const getLocationPathName = () => window.location.pathname

  const needToFillJobDetails = () => getLocationPathName().includes("/job/")

  useEffect(() => {
    if (needToFillJobDetails()) {
      getGigDetails()
    } else {
      gigService.getJobPostingQuota().then((q) => {
        setQuota(q)
        setDurationDays(q.freeDurationDays)
      }).catch(() => {})
      if (eventCityName) setLocation(eventCityName)
    }
    if (eventDateFromParam && eventDateToParam && !needToFillJobDetails()) {
      setContractType("DETERMINATE")
      setDateFrom(eventDateFromParam.substring(0, 10))
      setDateTo(eventDateToParam.substring(0, 10))
      if (!rateType) setRateType("DAY")
    }
  }, [])

  useEffect(() => {
    if (!gigDetails || !categories.length) return
    const categoryObj = categories.find((c) => c.name === gigDetails.category)
    if (!categoryObj) return
    gigService.getGigTitlesByCategory(categoryObj.id).then((titles: string[]) => {
      setPositions(titles.map((t) => ({ value: t, label: t })))
    })
  }, [categories, gigDetails])

  const updateJob = async () => {
    if (!ensureValid()) return

    setCreateGigIsLoading(true)

    try {
      const { fileNames, publicUrls } = await uploadImagesToR2(images)

      const newDateTo = dateTo ? new Date(dateTo + "T00:00:00") : null
      const isInactive = gigDetails && !gigDetails.enabled && gigDetails.enabled != null
      const updateGigRequest: gigService.UpdateGigRequest = {
        gigTitle: gigTitle!.label,
        location,
        category,
        rate,
        rateType,
        dateFrom: dateFrom ? dateFrom + "T00:00:00" : undefined,
        dateTo: dateTo ? dateTo + "T00:00:00" : undefined,
        cvNecessary,
        videoNecessary,
        description,
        negotiableRate,
        jobType,
        contractType,
        experience,
        confidentialRate,
        workLocationType,
        address,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        ...(isInactive && newDateTo && newDateTo > new Date() ? { enabled: true } : {}),
      }

      const response = await gigService.updateGigPresigned(gigId!, updateGigRequest, fileNames, publicUrls)

      if (response.ok) {
        notify("Job-ul a fost editat cu succes!", "success")
        navigate("/jobs")
      } else {
        notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
        navigate("/home")
      }
    } catch {
      notify("A apărut o eroare. Te rugăm să contactezi suportul.", "error")
      navigate("/home")
    } finally {
      setCreateGigIsLoading(false)
    }
  }

  const togglePromote = async (durationDays?: number) => {
    if (!gigId) return
    const res = await gigService.promoteGig(gigId, durationDays)
    if (res && (res.status === 422 || res.status === 400)) {
      setShowPromoteDurationModal(false)
      navigate("/credits")
      return
    }
    setIsPromoted((prev) => !prev)
    setShowPromoteDurationModal(false)
    refreshCredits()
  }

  const handlePromoteClick = () => {
    if (isPromoted) {
      togglePromote()
    } else {
      setShowPromoteDurationModal(true)
    }
  }

  const toggleJobStatus = async () => {
    const updateGigRequest: gigService.UpdateGigRequest = {}
    updateGigRequest.enabled = !(gigDetails?.enabled || gigDetails?.enabled == null)

    const formData = new FormData()
    formData.append(
      "body",
      new Blob([JSON.stringify(updateGigRequest)], { type: "application/json" })
    )

    const response = await gigService.updateGigDetails(formData, gigId)

    if (response.ok) {
      notify("Statusul a fost schimbat cu succes!", "success")
      navigate("/jobs")
    } else {
      let errorMsg = "A apărut o eroare. Te rugăm să contactezi suportul."
      try {
        const body = await response.json()
        if (body?.["error-message"]) errorMsg = body["error-message"]
      } catch {}
      notify(errorMsg, "error")
    }
  }

  const getGigDetails = async () => {
    const currentGigId = getLocationPathName().split("/")[2]
    const gig = (await gigService.getGigDetails(currentGigId)) as GigPreview

    setGigDetails(gig)
    setGigTitle({ value: gig.gigTitle, label: gig.gigTitle })
    setCategory(gig.category)
    setLocation(gig.location)
    setRateType(gig.rateType ?? "")
    setRate(gig.rate != null ? gig.rate.toString() : "")
    setDateFrom(gig.dateFrom ? gig.dateFrom.substring(0, 10) : "")
    setDateTo(gig.dateTo ? gig.dateTo.substring(0, 10) : "")
    setCvNecessary(gig.cvNecessary)
    setVideoNecessary(gig.videoNecessary)
    setDescription(gig.description)
    setGigId(gig.id)
    setJobType(gig.jobType ?? "")
    setContractType(gig.contractType ?? (gig.dateTo ? "DETERMINATE" : "INDETERMINATE"))
    setExperience(gig.experience ?? "")
    setConfidentialRate(gig.confidentialRate ?? false)
    setNegotiableRate(gig.negotiableRate ?? false)
    setWorkLocationType(gig.workLocationType ?? "")
    setAddress(gig.address ?? "")
    setLatitude(gig.latitude ?? null)
    setLongitude(gig.longitude ?? null)
    setIsPromoted(gig.promoted ?? false)
    if (gig.videoNecessary || (gig.workLocationType && gig.workLocationType !== "AT_ADDRESS" && gig.workLocationType !== "REMOTE")) {
      setShowAdvanced(true)
    }

    setLoading(true)
    const loadedFiles: File[] = []

    for (const image of gig.images) {
      const file = await getFileFromUrl(image, image)
      if (file) loadedFiles.push(file)
    }

    setImages(loadedFiles.slice(0, 5))
    setLoading(false)
  }

  async function getFileFromUrl(url: string, name: string, defaultType = "image/jpeg") {
    try {
      const response = await fetch(url)
      const data = await response.blob()
      return new File([data], name, { type: data.type || defaultType })
    } catch {
      return null
    }
  }

  const selectMenuProps = {
    menuPortalTarget: document.body,
    menuPosition: "fixed" as const,
    menuPlacement: "auto" as const,
    styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
  }

  const selectWrapper = (key: FieldKey, node: React.ReactNode) => (
    <div className={errors[key] ? "rounded-[6px] ring-1 ring-figmaContrast" : ""}>{node}</div>
  )

  // ---- Sections ----

  const renderSection = (num: number, title: string, subtitle: string, content: React.ReactNode) => (
    <section className="rounded-[16px] border border-figmaLightGray p-5 sm:p-6">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-figmaPrimary text-sm font-bold text-white">
          {num}
        </span>
        <div>
          <h4 className="text-figmaBlack text-base font-bold leading-tight">{title}</h4>
          {subtitle && <p className="text-xs text-figmaDarkGray">{subtitle}</p>}
        </div>
      </div>
      {content}
    </section>
  )

  const renderRoleSection = () => (
    <div className="space-y-5">
      <FormField label="Categorie" required error={errors.category}>
        {selectWrapper(
          "category",
          <Select {...selectMenuProps}
            placeholder="Selectează..."
            value={getCategoryOptions().find((o) => o.label === category) ?? null}
            onChange={handleCategoryChange}
            options={getCategoryOptions()}
          />
        )}
      </FormField>

      <FormField label="Poziție" required error={errors.gigTitle}>
        {selectWrapper(
          "gigTitle",
          <Select {...selectMenuProps}
            placeholder={category ? "Selectează poziția..." : "Selectează mai întâi categoria"}
            isClearable
            isDisabled={!category}
            options={positions}
            onChange={(newValue) => {
              setGigTitle(newValue)
              clearError("gigTitle")
            }}
            value={gigTitle}
          />
        )}
      </FormField>

      <FormField label="Experiență" required error={errors.experience}>
        {selectWrapper(
          "experience",
          <Select {...selectMenuProps}
            placeholder="Selectează..."
            value={EXPERIENCE_OPTIONS.find((o) => o.value === experience) ?? null}
            onChange={(newValue) => {
              setExperience(newValue?.value || "")
              clearError("experience")
            }}
            options={EXPERIENCE_OPTIONS}
          />
        )}
      </FormField>
    </div>
  )

  const renderDurationSection = (isEditMode: boolean) => (
    <div className="space-y-5">
      <FormField label="Tip perioadă" required error={errors.contractType}>
        <Segmented
          options={CONTRACT_TYPE_OPTIONS as unknown as { value: string; label: string }[]}
          value={contractType}
          onChange={handleContractTypeChange}
          disabled={!!eventMinDate}
        />
      </FormField>

      {contractType === "DETERMINATE" && (
        <FormField
          label="Perioadă"
          required
          error={errors.period}
          hint={
            eventMinDate && eventMaxDate
              ? `Interval permis: ${eventMinDate.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" })} — ${eventMaxDate.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" })}`
              : undefined
          }
        >
          <DateRangePicker
            label=""
            startDate={dateFrom ? new Date(dateFrom) : null}
            endDate={dateTo ? new Date(dateTo) : null}
            onChange={(start, end) => {
              setDateFrom(start ? toLocalDateString(start) : "")
              setDateTo(end ? toLocalDateString(end) : "")
              clearError("period")
            }}
            minDate={isEditMode ? null : (eventMinDate ?? new Date())}
            maxDate={eventMaxDate}
          />
        </FormField>
      )}

      {contractType === "INDETERMINATE" && (
        <FormField label="Data de început" hint="Opțional. Lasă gol dacă jobul începe imediat.">
          <input
            type="date"
            className="w-full rounded-[12px] bg-figmaLightGray px-5 py-[14px] text-[16px] text-figmaBlack"
            value={dateFrom}
            min={isEditMode ? undefined : todayLocalDateString()}
            onChange={(e) => {
              const val = (e.target as HTMLInputElement).value
              if (!isEditMode && val && val < todayLocalDateString()) return
              setDateFrom(val)
            }}
          />
        </FormField>
      )}
    </div>
  )

  const renderPaySection = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <JobbinInputGrey
            type="salariu"
            iconStart=""
            label="Cât plătești?"
            required={!confidentialRate}
            value={confidentialRate ? "" : rate}
            name="rate"
            placeholder={confidentialRate ? "Confidențial" : "30"}
            onChange={(e) => {
              setRate((e.target as HTMLInputElement).value)
              clearError("rate")
            }}
            disabled={confidentialRate}
          />
          {errors.rate && (
            <p className="mt-2 text-sm font-medium text-figmaContrast">{errors.rate}</p>
          )}
        </div>

        <FormField label="Pe" required error={errors.payUnit}>
          <Segmented
            options={PAY_UNIT_OPTIONS as unknown as { value: string; label: string }[]}
            value={payUnit}
            onChange={(unit) => {
              setRateType(composeRateType(unit, grossNet || "GROSS"))
              clearError("payUnit")
            }}
          />
        </FormField>
      </div>

      {payUnit === "MONTHLY" && (
        <FormField label="Brut sau net">
          <Segmented
            options={GROSS_NET_OPTIONS as unknown as { value: string; label: string }[]}
            value={grossNet}
            onChange={(gn) => setRateType(composeRateType("MONTHLY", gn))}
          />
        </FormField>
      )}

      <label className="flex cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 accent-figmaPrimary"
          checked={negotiableRate}
          onChange={(e) => setNegotiableRate(e.target.checked)}
        />
        <span className="text-figmaBlack jobbin-input">Salariul este negociabil</span>
      </label>

      <button
        type="button"
        onClick={() => {
          setConfidentialRate((v) => !v)
          clearError("rate")
        }}
        className="text-sm font-medium text-figmaDarkGray underline underline-offset-2 hover:text-figmaPrimary"
      >
        {confidentialRate ? "Afișează salariul public" : "Nu vreau să afișez salariul public"}
      </button>
    </div>
  )

  const renderLocationSection = () => (
    <div className="space-y-5">
      <FormField label="Oraș" required error={errors.location}>
        {selectWrapper(
          "location",
          <AsyncSelect {...selectMenuProps}
            placeholder="Caută oraș..."
            loadingMessage={() => "Se caută..."}
            noOptionsMessage={({ inputValue }) =>
              inputValue.length < 2 ? "Scrie cel puțin 2 caractere" : "Niciun oraș găsit"
            }
            value={location ? { value: location, label: location } : null}
            onChange={(newValue) => {
              setLocation(newValue?.label || "")
              clearError("location")
            }}
            loadOptions={loadCityOptions}
            isClearable
          />
        )}
      </FormField>

      <FormField
        label="Mod de lucru"
        required
        error={errors.workLocationType}
        hint="Nu precizez = jobul e la fața locului, dar nu publici adresa exactă."
      >
        <Segmented
          options={WORK_LOCATION_TYPE_OPTIONS as unknown as { value: string; label: string }[]}
          value={workLocationType}
          onChange={(val) => {
            setWorkLocationType(val)
            if (val !== "AT_ADDRESS") {
              setAddress("")
              setLatitude(null)
              setLongitude(null)
            }
            clearError("workLocationType")
            clearError("address")
          }}
        />
      </FormField>

      {workLocationType === "AT_ADDRESS" && (
        <FormField
          label="Adresă exactă"
          required
          error={errors.address}
          hint="Se afișează candidatului doar după ce accepți aplicarea."
        >
          <PlacesAutocomplete
            value={address}
            placeholder="Caută adresa exactă..."
            onPlaceSelected={(addr, lat, lng) => {
              setAddress(addr)
              setLatitude(lat)
              setLongitude(lng)
              clearError("address")
            }}
            onClear={() => {
              setAddress("")
              setLatitude(null)
              setLongitude(null)
            }}
          />
        </FormField>
      )}
    </div>
  )

  const renderDetailsSection = () => (
    <div className="space-y-5">
      <FormField label="Descriere" required error={errors.description}>
        <textarea
          className={`h-[200px] w-full rounded-[12px] bg-figmaLightGray px-5 py-[14px] text-[16px] ${
            errors.description ? "ring-1 ring-figmaContrast" : ""
          }`}
          onChange={(e) => {
            setDescription((e.target as HTMLTextAreaElement).value.slice(0, DESCRIPTION_MAX))
            clearError("description")
          }}
          maxLength={DESCRIPTION_MAX}
          placeholder={"Responsabilități:\n\nProgram:\n\nBeneficii:"}
          style={{ resize: "none" }}
          value={description}
        />
        <p
          className={`mt-1.5 text-right text-xs ${
            description.length >= DESCRIPTION_MAX ? "text-figmaContrast" : "text-figmaDarkGray"
          }`}
        >
          {description.length}/{DESCRIPTION_MAX}
        </p>
      </FormField>

      <div>
        <p className="text-figmaPrimary jobbin-input-label mb-1">Fotografii</p>
        <p className="text-xs text-figmaDarkGray mb-3">Adaugă până la 5 imagini reprezentative (opțional).</p>
        <div className="rounded-[16px] bg-figmaGray p-4 sm:p-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            {[0, 1, 2, 3, 4].map((index) => (
              <UploadAndDisplayImage
                key={index}
                onImageSelected={onImageSelected}
                onImageRemoved={onImageRemoved}
                alreadySelectedFile={images[index]}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderAdvancedSection = () => (
    <div className="rounded-[16px] border border-figmaLightGray">
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-figmaBlack font-bold">Mai multe opțiuni</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={`h-5 w-5 text-figmaDarkGray transition-transform ${showAdvanced ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {showAdvanced && (
        <div className="space-y-4 px-5 pb-5">
          <p className="text-xs text-figmaDarkGray">Ce trebuie să trimită candidatul când aplică.</p>
          <label className="flex cursor-pointer items-center justify-between gap-4 rounded-[12px] bg-figmaLightGray p-[14px]">
            <span className="text-figmaBlack jobbin-input">Cer CV la aplicare</span>
            <ToggleBox isChecked={cvNecessary} onColor="#486AF6" onToggle={setCvNecessary} />
          </label>
          <label className="flex cursor-pointer items-center justify-between gap-4 rounded-[12px] bg-figmaLightGray p-[14px]">
            <span className="text-figmaBlack jobbin-input">Cer video de prezentare la aplicare</span>
            <ToggleBox isChecked={videoNecessary} onColor="#486AF6" onToggle={setVideoNecessary} />
          </label>
        </div>
      )}
    </div>
  )

  const renderBackToList = () => (
    <button
      type="button"
      onClick={() => navigate(eventId ? `/events/${eventId}` : "/jobs")}
      className="mb-4 flex items-center gap-2 text-sm font-bold text-figmaBlack transition-opacity hover:opacity-70"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <path d="m15 18-6-6 6-6" />
      </svg>
      {eventId ? "Înapoi la eveniment" : "Înapoi la joburi"}
    </button>
  )

  const renderProgress = () => (
    <div className="mb-6">
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-figmaBlack text-sm font-bold">{STEP_TITLES[currentStep]}</p>
        <p className="text-figmaDarkGray text-sm font-medium">
          Pasul {currentStep + 1} din {STEP_TITLES.length}
        </p>
      </div>
      <div className="flex gap-1.5">
        {STEP_TITLES.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= currentStep ? "bg-figmaPrimary" : "bg-figmaLightGray"
            }`}
          />
        ))}
      </div>
    </div>
  )

  const renderForm = (isEditMode: boolean) => {
    const sectionContents = [
      renderRoleSection(),
      renderDurationSection(isEditMode),
      renderPaySection(),
      renderLocationSection(),
      renderDetailsSection(),
    ]
    const isLastStep = currentStep === STEP_TITLES.length - 1
    const selectedCost = quota?.durationOptions.find(o => o.days === durationDays)?.cost ?? 0
    // Abonat cu joburi incluse rămase (sau nelimitat): postarea consumă 1 job inclus, nu credite.
    const coveredByPlan = !!quota?.hasActivePlan && (quota?.includedJobPostsRemaining ?? 0) !== 0
    const includedUnlimited = quota?.includedJobPostsRemaining === -1
    const mustPayToPost = selectedCost > 0
    const notEnoughCredits = mustPayToPost && !!quota && quota.balance < selectedCost
    const postButtonText = coveredByPlan
      ? "Postează job (1 job inclus)"
      : mustPayToPost
      ? `Postează job (−${selectedCost} credite)`
      : "Postează job"

    return (
    <div>
      {renderBackToList()}
      <h3 className="page-title text-[24px] sm:text-[28px] md:text-[32px] mb-6">
        {isEditMode ? gigDetails?.gigTitle || "" : "Postează job"}
      </h3>

      {eventTitle && !isEditMode && (
        <div className="mb-6 flex items-center gap-3 bg-blue-50 border border-figmaPrimary rounded-2xl px-5 py-4">
          <div>
            <p className="text-xs text-figmaDarkGray font-medium">Job pentru evenimentul</p>
            <p className="text-figmaPrimary font-bold">{decodeURIComponent(eventTitle)}</p>
          </div>
        </div>
      )}

      <div className="max-w-3xl">
        <div className="space-y-6">
          {isEditMode ? (
            <>
              {renderSection(1, "Rol", "Ce post cauți să umpli", renderRoleSection())}
              {renderSection(2, "Durată", "Cât ține contractul", renderDurationSection(true))}
              {renderSection(3, "Plată", "Cât și pe ce unitate", renderPaySection())}
              {renderSection(4, "Locație", "Unde se desfășoară", renderLocationSection())}
              {renderSection(5, "Detalii", "Descriere și fotografii", renderDetailsSection())}
              {renderAdvancedSection()}

              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="w-full">
                  <JobbinPrimaryButton onClickAction={updateJob} text="Actualizează job" />
                </div>
                <div className="w-full">
                  <JobbinSecondaryButton
                    onClickAction={toggleJobStatus}
                    text={
                      gigDetails && (gigDetails.enabled || gigDetails.enabled == null)
                        ? "Dezactivează job"
                        : "Activează job"
                    }
                  />
                </div>
                <div className="w-full">
                  <JobbinSecondaryButton
                    onClickAction={handlePromoteClick}
                    text={isPromoted ? "Promovat — Anulează" : "Promovează job"}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {renderProgress()}
              {renderSection(
                currentStep + 1,
                STEP_TITLES[currentStep],
                STEP_SUBTITLES[currentStep],
                sectionContents[currentStep]
              )}
              {isLastStep && renderAdvancedSection()}

              {isLastStep && quota && (
                <div className="space-y-3">
                  <div>
                    <p className="text-figmaBlack font-semibold text-sm mb-1">Vizibilitatea anunțului</p>
                    <p className="text-figmaDarkGray text-xs mb-3">Cât timp rămâne jobul activ și vizibil candidaților</p>
                    <div className="grid grid-cols-3 gap-3">
                      {durationDaysOptions.map(o => (
                        <button
                          key={o.days}
                          type="button"
                          onClick={() => setDurationDays(o.days)}
                          className={`relative rounded-xl border-2 p-3 text-center transition-colors ${
                            durationDays === o.days
                              ? "border-figmaPrimary bg-figmaPrimary/5"
                              : "border-figmaLightGray hover:border-figmaDarkBg"
                          }`}
                        >
                          <p className="text-sm font-bold text-figmaBlack">{o.days} zile</p>
                          <p className="mt-1 text-xs font-semibold text-figmaPrimary">
                            {coveredByPlan ? "Inclus" : o.cost === 0 ? "Gratuit" : `${o.cost} credite`}
                          </p>
                        </button>
                      ))}
                    </div>
                    {durationCappedByDate && (
                      <p className="mt-2 text-xs text-figmaDarkGray">
                        Jobul se încheie pe {dateTo} — anunțul rămâne activ cel mult până atunci.
                      </p>
                    )}
                  </div>

                  {coveredByPlan ? (
                    <div className="rounded-[12px] bg-green-50 border border-green-200 px-4 py-3 text-sm">
                      <span className="font-bold text-green-700">
                        {includedUnlimited
                          ? "Joburi nelimitate în abonament"
                          : quota.includedJobPostsRemaining === 1
                          ? "Ai 1 job inclus rămas în abonament"
                          : `Ai ${quota.includedJobPostsRemaining} joburi incluse rămase în abonament`}
                      </span>
                      <span className="text-figmaDarkGray"> — această postare folosește 1 job inclus, nu consumă credite.</span>
                    </div>
                  ) : !mustPayToPost ? (
                    <div className="rounded-[12px] bg-blue-50 border border-figmaPrimary px-4 py-3 text-sm">
                      <span className="font-bold text-figmaPrimary">
                        {quota.freeRemaining === 1
                          ? "Ai 1 postare gratuită rămasă"
                          : `Ai ${quota.freeRemaining} postări gratuite rămase`}
                      </span>
                      <span className="text-figmaDarkGray"> — această postare ({durationDays} zile) nu consumă credite.</span>
                    </div>
                  ) : (
                    <div className={`rounded-[12px] px-4 py-3 text-sm border ${notEnoughCredits ? "bg-red-50 border-red-200" : "bg-figmaLightGray border-figmaLightGray"}`}>
                      <p className="text-figmaBlack">
                        Postarea pe {durationDays} zile costă <span className="font-bold">{selectedCost} credite</span>
                        <span className="text-figmaDarkGray"> · sold curent: {quota.balance}</span>
                      </p>
                      {quota.freeRemaining > 0 && durationDaysOptions.some(o => o.days === quota.freeDurationDays) && (
                        <button
                          type="button"
                          onClick={() => setDurationDays(quota.freeDurationDays)}
                          className="mt-1 block font-semibold text-figmaPrimary underline underline-offset-2"
                        >
                          Ai o postare gratuită — folosește-o pentru durata de {quota.freeDurationDays} zile
                        </button>
                      )}
                      {notEnoughCredits && (
                        <button
                          type="button"
                          onClick={() => navigate("/credits")}
                          className="mt-1 font-bold text-figmaContrast underline underline-offset-2"
                        >
                          Nu ai destule credite — cumpără un pachet
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {currentStep > 0 && (
                  <div className="sm:w-44">
                    <JobbinSecondaryButton onClickAction={goToPreviousStep} text="Înapoi" />
                  </div>
                )}
                <div className="sm:ml-auto sm:w-56">
                  {isLastStep ? (
                    <JobbinPrimaryButton onClickAction={doSendJob} text={postButtonText} />
                  ) : (
                    <JobbinPrimaryButton onClickAction={goToNextStep} text="Continuă" />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <PromoteDurationModal
        isOpen={showPromoteDurationModal}
        title="Promovează job"
        subtitle="Anunțul tău urcă sus în listă și e evidențiat candidaților."
        tiers={pricing?.promoteGig ?? []}
        balance={creditBalance}
        onConfirm={(d) => togglePromote(d)}
        onCancel={() => setShowPromoteDurationModal(false)}
      />
    </div>
    )
  }

  if (needToFillJobDetails()) {
    return (
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 overflow-y-auto">
        {createGigIsLoading || loading ? <LoadingComponent /> : renderForm(true)}
      </div>
    )
  }

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 overflow-y-auto">
      {createGigIsLoading ? <LoadingComponent /> : renderForm(false)}
    </div>
  )
}
